import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { APP_LOCATION } from '@core/config/app-location';
import { AppSurfaceService } from '@core/config/app-surface.service';
import { UserQuery } from '@core/store/user/user.query';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideBell } from '@ng-icons/lucide';
import {
  PriceRequestGroup,
  PriceRequestInbox,
} from '@shared/services/price-request-inbox.service';
import { EMPTY } from 'rxjs';
import { V2_ICONS } from '@v2/v2.icons';
import { kindIcon, kindToTab } from '@v2/shared/v2-price-requests';

/**
 * The price-request inbox, in the slot the top bar reserved for it.
 *
 * The slot's original comment said the bell was waiting on the flightcompass
 * feed (data-gaps G11) and that a client would otherwise see a permanently
 * empty bell. Both were true of that feed and neither is true of this: the
 * inbox is a domain feature over `PriceList` and `Handler` that has been live
 * on production throughout, and it is staff-only, so no client sees a bell at
 * all. See G21.
 *
 * The count is not "unread". Nothing here is read or unread -- it is how many
 * records currently need a refresh request, which is why it can go down
 * without anyone opening the popover.
 */
@Component({
  selector: 'app-v2-notification-bell',
  templateUrl: './v2-notification-bell.component.html',
  styleUrls: ['./v2-notification-bell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIcon],
  providers: [provideIcons({ ...V2_ICONS, lucideBell })],
})
export class V2NotificationBellComponent {
  private readonly userQuery = inject(UserQuery);
  private readonly router = inject(Router);
  private readonly inbox = inject(PriceRequestInbox);
  private readonly surface = inject(AppSurfaceService);
  private readonly location = inject(APP_LOCATION);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly user = toSignal(this.userQuery.getUser$, { initialValue: null });

  /**
   * The same predicate the rail uses (`v2NavGroups`), so who sees the bell and
   * who sees the admin section cannot drift apart.
   */
  protected readonly isStaff = computed(() => !!this.user()?.is_staff);

  /**
   * `EMPTY` on the server, deliberately.
   *
   * `toSignal` subscribes when the component is constructed, and subscribing
   * is what starts the service's five-minute poll -- `shareReplay({ refCount:
   * true })` means injection alone does nothing, so this is the only place the
   * guard can live. Guarding the template would not help: the markup is
   * `@if (isStaff())`, and the subscription happens whatever the template
   * renders.
   *
   * An interval opened during a server render never fires, never clears, and
   * holds the render open.
   */
  protected readonly groups = toSignal(this.isBrowser ? this.inbox.groups$ : EMPTY, {
    initialValue: [] as PriceRequestGroup[],
  });

  protected readonly count = computed(() =>
    this.groups().reduce((total, group) => total + group.candidates.length, 0),
  );

  /** Only the groups with something in them; an empty group is not news. */
  protected readonly liveGroups = computed(() =>
    this.groups().filter(group => group.candidates.length > 0),
  );

  protected readonly open = signal(false);

  protected readonly icon = kindIcon;

  protected toggle(): void {
    this.open.update(open => !open);
  }

  protected close(): void {
    this.open.set(false);
  }

  /**
   * Price requests are staff tooling and live on the estimator host only, so
   * from the briefings host this leaves the origin rather than navigating
   * in-app. An in-app navigate there would render admin screens on the
   * client-facing briefings domain -- counted in the briefings Plausible
   * property and offered to a crawler as canonical there.
   */
  protected openGroup(group: PriceRequestGroup): void {
    this.close();
    const tab = kindToTab(group.kind);

    if (this.surface.current !== 'estimator') {
      this.location?.replace(
        this.surface.urlFor('estimator', `/admin/price-requests?tab=${encodeURIComponent(tab)}`),
      );
      return;
    }

    void this.router.navigate(['/admin/price-requests'], { queryParams: { tab } });
  }
}
